/**
 * useToolboxEditor (#638).
 *
 * Drives ToolBox edit mode: the user sees every catalogue tool they're allowed
 * to use, toggles each on/off, and drags them into the order they want. The
 * saved profile is `enabledOrderedKeys` — the enabled tools in their current
 * display order.
 *
 * Initial ordering puts the active profile's enabled tools first (in profile
 * order), then the remaining catalogue tools (disabled) in catalogue order, so
 * the current toolbox reads top-to-bottom and extras are available below.
 *
 * The catalogue passed in is already permission-filtered (canDo), so any
 * profile key missing from it — a tool the user no longer has access to — is
 * silently dropped, matching the "loading skips inaccessible tools" rule.
 */

import { useCallback, useMemo, useState } from 'react';
import type { CatalogueTool } from './toolboxCatalogue';

export interface ToolboxEditorItem {
  tool: CatalogueTool;
  enabled: boolean;
}

export interface UseToolboxEditorReturn {
  items: ToolboxEditorItem[];
  /** The enabled tools, in current display order — the value to persist. */
  enabledOrderedKeys: string[];
  /** Enable/disable a tool by key. */
  toggle: (key: string) => void;
  /** Apply a new full ordering (all catalogue keys, drag-reorder output). */
  reorder: (orderedKeys: string[]) => void;
  /** Disable every tool — start building a toolbox from scratch. */
  clear: () => void;
}

/** Build the initial ordered/enabled item list from a catalogue + a profile's tool keys. */
function buildInitialItems(
  catalogue: CatalogueTool[],
  profileToolKeys: string[],
): ToolboxEditorItem[] {
  const byKey = new Map(catalogue.map((t) => [t.key, t]));
  const enabledKeys = profileToolKeys.filter((k) => byKey.has(k));
  const enabledSet = new Set(enabledKeys);

  const enabledItems: ToolboxEditorItem[] = enabledKeys.map((k) => ({
    tool: byKey.get(k)!,
    enabled: true,
  }));
  const remainingItems: ToolboxEditorItem[] = catalogue
    .filter((t) => !enabledSet.has(t.key))
    .map((t) => ({ tool: t, enabled: false }));

  return [...enabledItems, ...remainingItems];
}

export function useToolboxEditor(
  catalogue: CatalogueTool[],
  profileToolKeys: string[],
): UseToolboxEditorReturn {
  const [items, setItems] = useState<ToolboxEditorItem[]>(() =>
    buildInitialItems(catalogue, profileToolKeys),
  );

  const toggle = useCallback((key: string) => {
    setItems((prev) =>
      prev.map((it) => (it.tool.key === key ? { ...it, enabled: !it.enabled } : it)),
    );
  }, []);

  const reorder = useCallback((orderedKeys: string[]) => {
    setItems((prev) => {
      const byKey = new Map(prev.map((it) => [it.tool.key, it]));
      const next: ToolboxEditorItem[] = [];
      for (const key of orderedKeys) {
        const it = byKey.get(key);
        if (it) next.push(it);
      }
      // Keep any items not named in orderedKeys (defensive), in their prior order.
      for (const it of prev) {
        if (!orderedKeys.includes(it.tool.key)) next.push(it);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems((prev) => prev.map((it) => (it.enabled ? { ...it, enabled: false } : it)));
  }, []);

  const enabledOrderedKeys = useMemo(
    () => items.filter((it) => it.enabled).map((it) => it.tool.key),
    [items],
  );

  return { items, enabledOrderedKeys, toggle, reorder, clear };
}
